/**
 * WorldList — the SOLE world-list accessor for the pre-game surfaces.
 *
 * The funnel (tickets 2026-07-12-004-ENGINE / 2026-07-11-013-ENGINE): the shell
 * lobby and the VP fast-path both need to read the world list, and both used to
 * duplicate the SAME broken lookup — "fetch page 0, scan it" — so ONE bug
 * (fresh worlds land on page 1+ of an unsorted list) produced TWO symptoms: the
 * lobby silently omitted a freshly created world, and fast-path resume stalled
 * at DISCONNECTED for any world beyond page 0.
 *
 * This module is the one place either surface talks to `/api/worlds/list`, so
 * they can't drift again. Two operations, each read-honest (never a silent
 * page-0 truncation):
 *
 *   fetchAllOpenWorlds({database, includeTest}) — BROWSE. Paginates to
 *       exhaustion (bounded by MAX_PAGES) so every open world is visible.
 *       Returns {worlds, truncated}. The lobby renders `worlds`; `truncated`
 *       lets it say so honestly if the (very high) page cap is ever hit.
 *       Test-tagged worlds are EXCLUDED by default ([2026-08-07-004-QASTI]).
 *
 *       BROWSE vs RESOLVE is the axis that decides test-world visibility: a
 *       stranger scanning a list must not be offered developer state, but
 *       someone who typed a world's exact name has already opted in. Browse
 *       hides; resolve finds.
 *
 *   resolveWorldByName(handle, {database}) — RESOLVE. Uses the engine-owned
 *       server-side `name=` exact-match filter (WMS applies it to the full set
 *       BEFORE pagination), so a world is found regardless of which page the
 *       unsorted set would have placed it on — no page scanning. Returns the
 *       matching world object or null (a null is a genuine no-match the caller
 *       surfaces visibly, never a hang).
 */

import WorldCommands from './world-commands.js';

// Bounded browse: request the largest page the WMS route allows (limit<=100)
// and loop while has_more. 25 pages * 100 = 2500 worlds is far past any real
// stack; the cap exists only so a server bug can't spin an unbounded loop.
const PAGE_LIMIT = 100;
const MAX_PAGES = 25;

function _db(opts) {
    // Prefer an explicit database; otherwise the caller's selected DB; else
    // null -> WMS queries every universe. Never send the literal string "null".
    const d = (opts && opts.database != null)
        ? opts.database
        : (WorldCommands && WorldCommands.state ? WorldCommands.state.selectedDatabase : null);
    return (d == null || d === '') ? null : d;
}

function _params(base, database) {
    const p = { ...base };
    if (database != null) p.database = database;
    return p;
}

const WorldList = {
    /**
     * Fetch EVERY open world (paginate to exhaustion). Read-honest browse: the
     * lobby shows all worlds, so a fresh one on page 1+ is never hidden.
     *
     * @returns {Promise<{worlds: Array, truncated: boolean}>}
     */
    async fetchAllOpenWorlds(opts = {}) {
        const database = _db(opts);
        // BROWSE hides test worlds; RESOLVE does not. The server default is
        // already "hidden" ([2026-08-07-004-QASTI]), so browse simply doesn't
        // ask — but `resolveWorldByName` falls through to this function on a
        // name miss and MUST be able to reach a test world, or the VP fast-path
        // can no longer resume into the world it just created.
        const includeTest = opts.includeTest === true;
        const worlds = [];
        let truncated = false;

        for (let page = 0; page < MAX_PAGES; page++) {
            let resp;
            try {
                resp = await WorldCommands.callWorldAPI('/api/worlds/list', 'GET',
                    _params(
                        includeTest
                            ? { page, limit: PAGE_LIMIT, include_test: true }
                            : { page, limit: PAGE_LIMIT },
                        database
                    ));
            } catch (err) {
                console.error(`[world-list] page ${page} fetch failed:`, err);
                // Return what we have rather than throwing: a partial list beats
                // a blank lobby. Flag as truncated so honesty is preserved.
                truncated = true;
                break;
            }

            const pageWorlds = (resp && resp.raw_worlds) || [];
            worlds.push(...pageWorlds);

            if (!(resp && resp.has_more)) {
                return { worlds, truncated };
            }

            if (page === MAX_PAGES - 1) {
                // has_more still true at the cap: a genuinely huge stack. Say so.
                truncated = true;
                console.warn(
                    `[world-list] page cap (${MAX_PAGES}) reached with more worlds ` +
                    `available; showing first ${worlds.length}`
                );
            }
        }

        return { worlds, truncated };
    },

    /**
     * Resolve a world by exact (case-insensitive) name or world_id, page-
     * independent, via the server-side `name=` filter. No page scanning.
     *
     * @param {string} handle world name or world_id
     * @returns {Promise<object|null>} the world object, or null on no-match
     */
    async resolveWorldByName(handle, opts = {}) {
        const h = (handle == null ? '' : String(handle)).trim();
        if (!h) return null;
        const database = _db(opts);

        let resp;
        try {
            // The server filter matches on world NAME. Pass the handle as name;
            // if the handle was actually a world_id we still catch it in the
            // client-side id check below (the filtered set is tiny or empty).
            // include_test: naming a world exactly IS the opt-in. Hiding a
            // world from someone who typed its name would break VP fast-path
            // resume and turn "your world vanished" into the new silent
            // failure ([2026-08-07-004-QASTI]).
            resp = await WorldCommands.callWorldAPI('/api/worlds/list', 'GET',
                _params({ name: h, limit: PAGE_LIMIT, include_test: true }, database));
        } catch (err) {
            console.error(`[world-list] name resolve failed for "${h}":`, err);
            return null;
        }

        const worlds = (resp && resp.raw_worlds) || [];
        const needle = h.toLowerCase();
        // Match the LABEL or the ID ([2026-08-06-012-XSTITCH]): a human types
        // the display name, the fast-path carries the id, and `w.name` is now
        // the id too. ⚠️ Display names are NOT unique, so `find` may be picking
        // one of several — see the server-side ambiguity warning in
        // service_core.list_worlds. A caller that must be exact should hold the
        // id, not the label.
        const match = worlds.find((w) =>
            (w.display_name || '').toLowerCase() === needle ||
            (w.name || '').toLowerCase() === needle ||
            (w.world_id || '').toLowerCase() === needle
        );
        if (match) return match;

        // Server name-filter returned nothing (or only near-misses). The handle
        // may be a world_id, which the NAME filter can't match — fall back to a
        // single unfiltered exhaustion scan and match by id/name there. This is
        // the only path that pulls pages, and only on a name-miss.
        const { worlds: all } = await this.fetchAllOpenWorlds({ database, includeTest: true });
        const idMatch = all.find((w) =>
            (w.world_id || '').toLowerCase() === needle ||
            (w.name || '').toLowerCase() === needle ||
            (w.display_name || '').toLowerCase() === needle
        );
        return idMatch || null;
    },
};

if (typeof window !== 'undefined') {
    window.WorldList = WorldList;
}

export default WorldList;
